import { create } from 'zustand';
import type { ContractedService } from '~/domain/Assegurou/contractedServices';
import { LocalStorage } from '~/storage/LocalStorage';
import { ANONYMOUS_USER, USER_INFO } from '~/storage/constants';
import { getContractedServicesUseCase } from '~/useCases/AssegurouUseCases/GetContractedServicesUseCase';
import { MarisaAppUtils } from '~/utils/MarisaAppUtils';

export interface UserType {
  id: string;
  token: string;
  document: string;
  email: string;
  name: string;
  phone?: string;
  contractedServices: ContractedService[];
}

interface UserInfoFormState {
  user: UserType | null;
  setUserInfo: (user: UserType) => Promise<void>;
  deleteUserInfo: () => Promise<void>;
  logout: () => Promise<void>;
  customLogout?: () => Promise<void>;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  loadContractedServices: (props?: { noLoading?: boolean }) => Promise<void>;
}

export const useUserInfoStore = create<UserInfoFormState>((set, get) => ({
  user: null,
  setUserInfo: async (user) => {
    set({ user });
    await LocalStorage.remove(ANONYMOUS_USER);
    await LocalStorage.set(USER_INFO, user);
  },
  deleteUserInfo: async () => {
    set({ user: null });
    await LocalStorage.remove(USER_INFO);
  },
  logout: async () => {
    await LocalStorage.remove(ANONYMOUS_USER);
    await get().deleteUserInfo();
    const customLogout = get().customLogout;
    if (customLogout) await customLogout();
  },
  customLogout: undefined,
  loading: false,
  setLoading: (loading) => {
    set({ loading });
  },
  loadContractedServices: async ({ noLoading } = { noLoading: false }) => {
    if (MarisaAppUtils.disableInsurance) {
      return;
    }
    const userInfo = get().user;
    const loading = get().loading;

    if (userInfo && !loading) {
      if (!noLoading) get().setLoading(true);

      const contractedServicesResponse = await getContractedServicesUseCase(
        userInfo.document
      );
      const currentUser = get().user;
      if (
        JSON.stringify(contractedServicesResponse) !==
          JSON.stringify(get().user?.contractedServices) &&
        currentUser
      ) {
        get().setUserInfo({
          ...currentUser,
          contractedServices: contractedServicesResponse,
        });
      }

      if (!noLoading) get().setLoading(false);
    }
  },
}));
